import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { BackIcon, CircleCloseIcon } from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import { VoidFn } from 'types/baseTypes';
import { initialAddressState } from 'containers/customers/constants';
import { WebviewFunctions } from 'constants/common';
import { ErrorScreenWithRetry, Loader } from 'components';
import {
  useCreateCustomerCardSetupIntentMutation,
  useDeleteCustomerCardMutation,
  useSaveCustomerCardMutation
} from 'containers/customers/api';
import StripeCardComponent from 'containers/customers/components/StripeCardComponent';
import CustomInput, { InputType } from 'components/custom-input.tsx/CustomInput';
import AddressSection, { AddressSectionRef } from '../../invoices/components/AddressSection';
import { AddressField } from '../../invoices/invoices.model';
import StripeService from 'services/stripe';
import { showNotification } from 'utils/Notifier';
import { NotifierTypes } from 'constants/notifierConstants';
import { useAppDispatch } from 'hooks';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsCardOnFile } from 'containers/invoices/card-on-file/events';
import { ANALYTICS_EVENT_NAMES, ANALYTICS_EVENT_TYPES } from 'constants/webview';

interface CustomerAddCOFContentProps {
  handleClose: VoidFn;
  isWebView?: boolean;
  showHeader?: boolean;
  className?: string;
  contentClassName?: string;
  customerId?: string;
  teamId?: string;
  customerName?: string;
  customerEmail?: string;
  customerBillingAddress?: AddressField;
  cardId?: string;
  successCallback: VoidFn;
}

const CustomerAddCOFContent: FC<CustomerAddCOFContentProps> = ({
  handleClose,
  isWebView,
  className = '',
  contentClassName = '',
  customerId,
  teamId,
  customerName,
  customerEmail,
  customerBillingAddress,
  cardId,
  successCallback
}) => {
  const dispatch = useAppDispatch();

  const [billingAddress, setBillingAddress] = useState<AddressField>(customerBillingAddress || initialAddressState);
  const [cardSaveSuccess, setCardSaveSuccess] = useState(false);
  const [triggerCardSubmit, setTriggerCardSubmit] = useState(false);
  const [isCardProcessing, setIsCardProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState(customerName);
  const [cardholderEmail, setCardholderEmail] = useState(customerEmail);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const addressSectionRef = useRef<AddressSectionRef>(null);

  const [createSetupIntent, { isError: isSetupIntentError, isLoading: isSetupIntentLoading }] =
    useCreateCustomerCardSetupIntentMutation();

  const [saveCustomerCard, { isLoading: isSavingCard }] = useSaveCustomerCardMutation();
  const [deleteCustomerCard, { isLoading: isDeletingCard }] = useDeleteCustomerCardMutation();
  const title = cardId ? 'Update card on file' : 'Add card on file';

  useEffect(() => {
    setStripePromise(StripeService.getInstance());
  }, []);

  const handleCreateSetupIntent = () => {
    if (teamId && customerId) {
      createSetupIntent({
        teamId,
        customerId
      });
    }
  };

  useEffect(() => {
    handleCreateSetupIntent();
  }, [teamId, customerId]);

  useEffect(() => {
    // Log modal open event
    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFAddCardModalOpen, {
      'Customer Id': customerId,
      'Is Edit': !!cardId
    });
  }, []);

  useEffect(() => {
    if (isWebView) {
      // Notify mobile app that webview has loaded
      if (window?.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler(WebviewFunctions.WEBVIEW_LOADED);
      }
    }
  }, [isWebView]);

  const handleSave = async () => {
    // TODO: remove this if not needed
    if (cardSaveSuccess) {
      handleClose();
    } else {
      // Validate billing address (mandatory) before proceeding
      if (!addressSectionRef.current) {
        return; // Should not happen, but fail safe
      }

      const isAddressValid = await addressSectionRef.current.validateAddress();
      if (!isAddressValid) {
        return; // Don't proceed if address validation fails
      }
    }

    // Trigger Stripe form submission by setting the flag
    setTriggerCardSubmit(true);
    // }
  };

  const handleCardSaveSuccess = async (paymentMethodId?: string) => {
    setCardSaveSuccess(true);
    setTriggerCardSubmit(false); // Reset the submit trigger

    // Call save card API if we have the required data
    if (paymentMethodId && teamId && customerId) {
      try {
        const saveResult = await saveCustomerCard({
          teamId,
          customerId,
          paymentMethodId,
          cardHolderName: cardholderName ?? '',
          email: cardholderEmail ?? ''
        }).unwrap();

        showNotification(dispatch, NotifierTypes.SUCCESS, 'Card saved successfully');

        logAnalyticEvent(CleverTapEventsCardOnFile.webCOFAddCardSuccess, {
          'Customer Id': customerId,
          'Card Network': saveResult?.network,
          'Card Last 4': saveResult?.cardLast4,
          'Is Edit': !!cardId
        });

        if (saveResult?.id && window?.flutter_inappwebview) {
          window.flutter_inappwebview.callHandler(WebviewFunctions.UPDATE_COF, customerId);
          window.flutter_inappwebview.callHandler(WebviewFunctions.TRACK_ANALYTICS_EVENT, {
            eventName: ANALYTICS_EVENT_NAMES.ADD_CARD_ON_FILE_WEBVIEW,
            properties: {
              type: ANALYTICS_EVENT_TYPES.ADD_CARD_ON_FILE_SUCCESS
            }
          });
        }
        // If this is an edit operation (cardId exists) and save was successful, delete the old card
        if (cardId && saveResult?.id) {
          try {
            await deleteCustomerCard({
              teamId: Number(teamId),
              customerId,
              cardId
            }).unwrap();
          } catch (deleteError) {
            //error
          }
        }
      } catch (error) {
        // Reset the card save success state and trigger a new setup intent
        // This allows the user to try again with a fresh card form
        const { message, details } = error?.data || { message: 'Something went wrong', details: 'Please try again.' };
        const errorMessage = `${message}. ${details}`;
        showNotification(dispatch, NotifierTypes.ERROR, errorMessage);

        logAnalyticEvent(CleverTapEventsCardOnFile.webCOFAddCardFailed, {
          'Customer Id': customerId,
          'Error Message': errorMessage,
          'Is Edit': !!cardId
        });

        if (window?.flutter_inappwebview) {
          window.flutter_inappwebview.callHandler(WebviewFunctions.TRACK_ANALYTICS_EVENT, {
            eventName: ANALYTICS_EVENT_NAMES.ADD_CARD_ON_FILE_WEBVIEW,
            properties: {
              type: ANALYTICS_EVENT_TYPES.ADD_CARD_ON_FILE_FAILED
            }
          });
        }
        setCardSaveSuccess(false);
        setTriggerCardSubmit(false);
        // handleCreateSetupIntent();
        return;
      }
    }
    successCallback();
  };

  const handleCardSaveError = () => {
    setTriggerCardSubmit(false); // Reset the submit trigger on error
    // Error handling is already done in the StripeCOFComponent

    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFAddCardStripeFailed, {
      'Customer Id': customerId,
      'Is Edit': !!cardId
    });
    if (window?.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler(WebviewFunctions.TRACK_ANALYTICS_EVENT, {
        eventName: ANALYTICS_EVENT_NAMES.ADD_CARD_ON_FILE_WEBVIEW,
        properties: {
          type: ANALYTICS_EVENT_TYPES.ADD_CARD_ON_FILE_STRIPE_FAILED
        }
      });
    }
  };

  const handleCardProcessingChange = (isProcessing: boolean) => {
    setIsCardProcessing(isProcessing || isSavingCard || isDeletingCard);
  };

  const handleRetry = () => {
    handleCreateSetupIntent();
  };

  const handleAddressChange = useCallback((address: AddressField) => {
    setBillingAddress(address);
  }, []);

  const handleNameChange = (value: string) => {
    setCardholderName(value);
  };

  const handleEmailChange = (value: string) => {
    setCardholderEmail(value);
  };

  const handleBack = () => {
    handleClose();
  };

  // Determine styling based on context
  const containerClass = isWebView ? `h-screen w-screen bg-white ${className}` : `flex h-full flex-col ${className}`;

  const contentClass = isWebView
    ? `max-h-none px-6 pt-6 ${contentClassName}`
    : `max-h-[70vh] flex-1 overflow-y-auto px-6 pb-6 ${contentClassName}`;

  const fieldClassName = isWebView ? 'h-12 p-3' : 'h-[50px] p-4';
  return (
    <div className={containerClass}>
      <div
        className={`flex shrink-0 items-start justify-between p-6 pb-4 ${
          isWebView ? 'border-b border-borderGray' : ''
        }`}>
        <div className="flex flex-1 items-start">
          {isWebView && (
            <BackIcon
              className="path path-stroke-current mt-1 mr-2 shrink-0 cursor-pointer text-[#505050]"
              onClick={handleBack}
            />
          )}
          <div className="flex flex-col gap-1">
            <h2 className={`${isWebView ? 'text-17px' : 'text-xl'} font-bold text-headingGray`}>{title}</h2>
            {isWebView && <h2 className="text-sbase font-medium text-headingGray">{customerName}</h2>}
          </div>
        </div>
        {!isWebView && (
          <CircleCloseIcon
            onClick={handleBack}
            className="ml-4 shrink-0 cursor-pointer text-accentText hover:text-primaryText"
          />
        )}
      </div>

      {/* Loading and error states below title */}
      {isSetupIntentLoading && (
        <div className={`flex ${isWebView ? 'h-[40vh]' : 'min-h-[30vh]'} flex-1 items-center justify-center`}>
          <Loader />
        </div>
      )}
      {!isSetupIntentLoading && isSetupIntentError && (
        <div className={`flex ${isWebView ? 'h-[40vh]' : 'min-h-[30vh]'} flex-1 items-center justify-center`}>
          <ErrorScreenWithRetry handleReload={handleRetry} />
        </div>
      )}
      {!isSetupIntentLoading && !isSetupIntentError && (
        <>
          {/* Scrollable Content Area */}
          <div className={contentClass}>
            {/* Subtitle */}
            <p className="mb-6 text-sbase font-normal leading-relaxed text-primaryText">
              Make sure your customer has given you explicit consent to save their card for payments. Keep a copy of
              their signed approval to protect yourself against disputes and chargebacks.
            </p>

            {/* Stripe Card Element */}
            {stripePromise && (
              <Elements stripe={stripePromise}>
                <StripeCardComponent
                  onSuccess={handleCardSaveSuccess}
                  onError={handleCardSaveError}
                  onProcessingChange={handleCardProcessingChange}
                  shouldSubmit={triggerCardSubmit}
                  className={`py-3.5 ${fieldClassName}`}
                  billingAddress={billingAddress}
                  cardholderName={cardholderName}
                  cardholderEmail={cardholderEmail}
                />
              </Elements>
            )}
            <div className="my-5 space-y-5">
              <CustomInput
                id="cardholder-name"
                label="Cardholder's name"
                type={InputType.TEXT}
                placeholder="Enter cardholder name"
                value={cardholderName}
                onChange={handleNameChange}
                maxLength={50}
                className={`border border-borderGray bg-white outline-none ${fieldClassName}`}
              />
              <CustomInput
                id="cardholder-email"
                label="Cardholder's email"
                type={InputType.EMAIL}
                placeholder="Enter cardholder email"
                value={cardholderEmail}
                onChange={handleEmailChange}
                maxLength={300}
                className={`border border-borderGray bg-white outline-none ${fieldClassName}`}
              />
            </div>
            {/* Address Section */}
            <div className="pb-2 text-sbase font-semibold text-headingGray">Billing address</div>
            <AddressSection
              ref={addressSectionRef}
              onAddressChange={handleAddressChange}
              initialAddress={billingAddress}
              inputClassName={`bg-white ${fieldClassName}`}
            />
          </div>

          {/* Footer - Fixed */}
          <div className="flex shrink-0 justify-end gap-3 p-6 pt-4">
            {!isWebView && (
              <CustomButton
                id="cof-content-back-btn"
                type={ButtonType.SECONDARY}
                onClick={handleBack}
                className="h-10 w-[100px] px-6">
                Back
              </CustomButton>
            )}
            <CustomButton
              id="cof-content-save-btn"
              type={ButtonType.PRIMARY}
              onClick={handleSave}
              isLoading={isCardProcessing}
              customLottieClass="h-10 w-10"
              disabled={isCardProcessing || isSavingCard || isDeletingCard}
              className={` ${isWebView ? 'h-12 w-full' : 'h-10 w-[100px]'} px-6`}>
              {cardSaveSuccess ? 'Done' : 'Save'}
            </CustomButton>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerAddCOFContent;
